const COLLAPSED_CLASS = 'sleek-v2-sidebar-collapsed';
const COLLAPSED_STORAGE_KEY = 'casino.sleek_v2.sidebar_collapsed';
const DESKTOP_QUERY = '(min-width: 1025px)';

function onDomReady(callback: () => void): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
        return;
    }

    callback();
}

function readCollapsedState(): boolean {
    try {
        return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
    } catch (error) {
        return false;
    }
}

function writeCollapsedState(collapsed: boolean): void {
    try {
        window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch (error) {
        // Persisting the preference is optional; the visual state can still update.
    }
}

function applyCollapsedState(desktopQuery: MediaQueryList, collapsed: boolean): void {
    document.body.classList.toggle(COLLAPSED_CLASS, desktopQuery.matches && collapsed);

    document.querySelectorAll<HTMLElement>('[data-sleek-v2-sidebar-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
}

function bindSidebarCollapse(): void {
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);

    document.addEventListener(
        'click',
        (event) => {
            if (!(event.target instanceof Element)) {
                return;
            }

            const collapsedDropdownHeader = event.target.closest('.sidebar .dropdown-header');

            if (collapsedDropdownHeader && desktopQuery.matches && document.body.classList.contains(COLLAPSED_CLASS)) {
                event.preventDefault();
                event.stopPropagation();
                writeCollapsedState(false);
                applyCollapsedState(desktopQuery, false);

                return;
            }

            const toggle = event.target.closest('[data-sleek-v2-sidebar-toggle]');

            if (!toggle) {
                return;
            }

            const collapsed = !document.body.classList.contains(COLLAPSED_CLASS);
            writeCollapsedState(collapsed);
            applyCollapsedState(desktopQuery, collapsed);
        },
        true,
    );

    desktopQuery.addEventListener('change', () => applyCollapsedState(desktopQuery, readCollapsedState()));
    applyCollapsedState(desktopQuery, readCollapsedState());
}

function normalizeMenuSearchValue(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
}

function syncSidebarMenuSearch(input: HTMLInputElement): void {
    const sidebar = input.closest<HTMLElement>('.sidebar');

    if (!sidebar) {
        return;
    }

    const query = normalizeMenuSearchValue(input.value);
    const sections = sidebar.querySelectorAll<HTMLElement>('.menu-section');

    sections.forEach((section) => {
        let sectionHasVisibleItem = false;
        const items = section.querySelectorAll<HTMLElement>(':scope > .menu-btn, :scope > .dropdown-wrapper');

        items.forEach((item) => {
            const header = item.matches('.dropdown-wrapper')
                ? item.querySelector<HTMLElement>('.dropdown-header')
                : item;
            const headerMatches = normalizeMenuSearchValue(header?.textContent ?? '').includes(query);
            const children = item.matches('.dropdown-wrapper')
                ? item.querySelectorAll<HTMLElement>('.dropdown-link')
                : [];
            let childMatches = false;

            children.forEach((child) => {
                const matches = query === '' || headerMatches || normalizeMenuSearchValue(child.textContent ?? '').includes(query);
                child.hidden = !matches;
                childMatches = childMatches || matches;
            });

            const matches = query === '' || headerMatches || childMatches;
            item.hidden = !matches;
            sectionHasVisibleItem = sectionHasVisibleItem || matches;
        });

        section.hidden = query !== '' && !sectionHasVisibleItem;
    });
}

function bindSidebarMenuSearch(): void {
    document.querySelectorAll<HTMLInputElement>('[data-sidebar-menu-search]').forEach((input) => {
        if (input.dataset.sidebarMenuSearchInitialized === 'true') {
            return;
        }

        input.dataset.sidebarMenuSearchInitialized = 'true';
        input.addEventListener('input', () => syncSidebarMenuSearch(input));
        syncSidebarMenuSearch(input);
    });
}

export function initializeSidebarMenuInteractions(): void {
    onDomReady(() => {
        bindSidebarCollapse();
        bindSidebarMenuSearch();
    });
}
